// Small utility to interact with the site's localization form / selector
// Exposes `window.marketUtils.selectCountry(code)` and `window.marketUtils.getLocalizationForm()`
(function () {
  const _marketUtilsLog =
    typeof window.createLogger === 'function'
      ? window.createLogger('MarketUtils')
      : { log: () => {}, warn: () => {}, error: () => {} };

  // Map country ISO codes to their market default locale.
  const COUNTRY_LOCALE_MAP = {
    AT: 'en',
    BE: 'en',
    BG: 'en',
    HR: 'en',
    CZ: 'en',
    DK: 'en',
    EE: 'en',
    FI: 'en',
    GR: 'en',
    HU: 'en',
    IT: 'en',
    LV: 'en',
    LT: 'en',
    LU: 'en',
    NL: 'en',
    PL: 'en',
    PT: 'en',
    RO: 'en',
    SK: 'en',
    SI: 'en',
    ES: 'en',
    SE: 'en',
    GB: 'en',
    IM: 'en',
    JE: 'en',
    GG: 'en',
    DE: 'de',
    FR: 'fr',
    AE: 'en',
    SA: 'en',
    QA: 'en',
    BH: 'en',
    NO: 'en',
    CH: 'en',
    US: 'en'
  };

  // Home-market countries — served from the root domain with no locale prefix.
  const HOME_COUNTRIES = ['GB'];

  // Countries with a dedicated market subdomain (own locale prefix + country path).
  const DEDICATED_MARKET_COUNTRIES = ['DE', 'FR', 'AE', 'SA', 'QA', 'BH', 'NO', 'CH', 'US', 'JE', 'GG'];

  // URL prefix for the shared EU market.
  const SHARED_EU_MARKET_PATH = 'en-eu';

  function switchMarketURL(countryCode) {
    const country = countryCode.toUpperCase();

    // Strip any existing market folder from the current path
    let pathSegments = window.location.pathname.split('/').filter(Boolean);
    const marketFolderPattern = /^[a-z]{2,3}-[a-z]{2}$/i;
    _marketUtilsLog.log('switchMarketURL: current path segments=', pathSegments);
    if (pathSegments.length > 0 && marketFolderPattern.test(pathSegments[0])) {
      pathSegments.shift();
    }

    document.cookie = '_shopify_m=;path=/;domain=' + location.hostname + ';expires=Thu, 01 Jan 1970 00:00:00 GMT';

    const searchParams = window.location.search;
    let newPath;

    if (HOME_COUNTRIES.includes(country)) {
      // GB, JE, GG → root domain, no locale prefix
      newPath = '/' + (pathSegments.length ? pathSegments.join('/') : '');
      _marketUtilsLog.log('Home market selected, navigating to root:', newPath);
    } else if (DEDICATED_MARKET_COUNTRIES.includes(country)) {
      // DE → /de-de/…  FR → /fr-fr/…
      const locale = COUNTRY_LOCALE_MAP[country];
      const targetPath = locale + '-' + country.toLowerCase();
      newPath = '/' + targetPath + (pathSegments.length ? '/' + pathSegments.join('/') : '');
      _marketUtilsLog.log('switchMarketURL: dedicated market, navigating to', newPath);
    } else {
      // All other EU countries → shared EU market /en-eu/
      newPath = '/' + SHARED_EU_MARKET_PATH + (pathSegments.length ? '/' + pathSegments.join('/') : '');
      _marketUtilsLog.log('switchMarketURL: shared EU market, navigating to', newPath);
    }

    window.location.href = newPath + searchParams;
  }

  function getLocalizationFormElement() {
    // Prefer the custom element if present
    const lf = document.querySelector('localization-form');
    if (lf) return lf;

    // Fallback to common form selectors
    return (
      document.querySelector('form.localization-form') ||
      document.querySelector('localization-form form') ||
      document.querySelector('form[action*="/localization"]') ||
      null
    );
  }

  function selectCountry(code) {
    if (!code) return false;
    const countryCodeSelected = code.toString().toUpperCase();
    _marketUtilsLog.log('selectCountry called with code:', countryCodeSelected);
    const lf = getLocalizationFormElement();
    _marketUtilsLog.log('Localization form found:', lf);
    if (!lf) return false;

    try {
      // If the element is the custom element, try clicking the anchor option (mimics native UX)
      if (lf.tagName && lf.tagName.toLowerCase() === 'localization-form') {
        const option = lf.querySelector(`a[data-value="${countryCodeSelected}"]`);
        if (option) {
          option.click();
          _marketUtilsLog.log('Clicked localization option for country code:', countryCodeSelected);
          return true;
        }

        // if no option anchor, try to find enclosed form and set inputs manually
        const form = lf.querySelector('form');
        if (form) {
          _marketUtilsLog.warn(
            'Localization form is a custom element but no anchor option found, attempting to set input and submit'
          );
          const _setInput = (name, value) => {
            let el = form.querySelector(`input[name="${name}"]`);
            if (!el) {
              el = document.createElement('input');
              el.type = 'hidden';
              el.name = name;
              form.appendChild(el);
            }
            el.value = value;
          };
          _setInput('country_code', countryCodeSelected);
          _setInput('locale_code', 'en');
          _setInput('return_to', window.location.pathname + window.location.search);
          HTMLFormElement.prototype.submit.call(form);
          return true;
        } else {
          _marketUtilsLog.warn(
            'Localization form structure not recognized for custom element, attempting fallback input and submit'
          );
        }
      } else {
        _marketUtilsLog.warn('Localization form is not a custom element, attempting to set input and submit');
      }

      // lf might be a form node
      const form =
        lf.tagName && lf.tagName.toLowerCase() === 'form' ? lf : lf.querySelector && lf.querySelector('form');
      if (form) {
        let input = form.querySelector('input[name="country_code"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'country_code';
          form.appendChild(input);
        }
        input.value = countryCodeSelected;
        _marketUtilsLog.log('Submitting localization form with country code:', countryCodeSelected);
        form.submit();
        return true;
      } else {
        _marketUtilsLog.warn('No form found within localization form element');
      }
    } catch (err) {
      _marketUtilsLog.warn('selectCountry failed', err);
      return false;
    }

    return false;
  }

  window.marketUtils = {
    getLocalizationForm: getLocalizationFormElement,
    selectCountry: selectCountry,
    switchMarketURL: switchMarketURL
  };
})();
